import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { EditMode, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { PreferredPharmacyHelper } from "@/ContactDirectory/Helpers/PreferredPharmacyHelper";
import { PreferredPharmacyValidator } from "@/ContactDirectory/Validators/PreferredPharmacyValidator";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";

interface IProfileViewControlsProps {
    model: IPatientUIModel
}

class ProfileViewControls extends React.Component<IProfileViewControlsProps, never> {
    private preferredPharmacyValidator: PreferredPharmacyValidator;

    constructor(props: IProfileViewControlsProps) {
        super(props);
        const patientContactId = patientStore.getCurrentPatientContactId();
        const patientAddresses = patientStore.PatientModels[patientContactId].Addresses;
        const currentEmployee = sessionStore.getCurrentEmployee();
        const preferredPharmacyHelper = new PreferredPharmacyHelper(patientAddresses, currentEmployee);
        this.preferredPharmacyValidator = new PreferredPharmacyValidator(preferredPharmacyHelper);
    }

    public render(): JSX.Element {
        const patientContactId = patientStore.getCurrentPatientContactId();
        const preferredPharmacy = patientStore.PatientProfileOtherIdentificationModels[patientContactId].get("PreferredPharmacy");
        const canSaveOther = !!preferredPharmacy?.getDirectoryPharmacy() || this.preferredPharmacyValidator.validate(preferredPharmacy);
        const canSaveClinicians = patientStore.isClinicianViewValid();
        const canSaveIdentifiers = patientStore.isIdentifiersViewValid();
        const canSaveContacts = patientStore.isContactsViewValid();
        const activeProfileTab = this.props.model.get("PatientProfileActiveTab");
        const lockPatientDemographicSaveButton = this.props.model.get("LockPatientDemographicSaveButton");
        const currentPatientContactId = this.props.model.get("PatientContactId");
        const relationships = patientStore.PatientProfileRelationshipsModels[currentPatientContactId];
        const hasRelationships = relationships.get("Relationships").count() > 0;

        return (
            <div className="pull-right">
                {/* DEMOGRAPHICS */}
                {activeProfileTab === ProfileTab.Demographic
                    && this.props.model.get("PatientProfileEditMode") === EditMode.View
                    && patientStore.CanEditPatient
                    && (
                        <ButtonControl
                            className="btn btn-primary pull-right"
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Demographics_EditClick)}
                            label={cacheStore.GetString("EDITLabel")}
                        />
                    )
                }

                {activeProfileTab === ProfileTab.Demographic
                    && this.props.model.get("PatientProfileEditMode") === EditMode.Edit
                    && (
                        <div>
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Demographics_CancelClick)}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                className="btn btn-primary"
                                onClick={() => {
                                    if (!lockPatientDemographicSaveButton) {
                                        sessionStore.dispatch(LocalPatientOperations.Profile.Demographics_SaveClick);
                                    }
                                }}
                                label={cacheStore.GetString("SaveLabel")}
                            />
                        </div>
                    )
                }

                {/* IDENTIFIERS */}
                {activeProfileTab === ProfileTab.Identifiers
                    && this.props.model.get("PatientProfileEditMode") === EditMode.View
                    && patientStore.CanEditPatient
                    && (
                        <ButtonControl
                            className="btn btn-primary pull-right"
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Identifiers_EditClick)}
                            label={cacheStore.GetString("EDITLabel")}
                        />
                    )
                }
                {activeProfileTab === ProfileTab.Identifiers
                    && this.props.model.get("PatientProfileEditMode") === EditMode.Edit
                    && (
                        <div>
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Identifiers_CancelClick)}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                disabled={!canSaveIdentifiers}
                                className="btn btn-primary"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Identifiers_SaveClick)}
                                label={cacheStore.GetString("SaveLabel")}
                            />
                        </div>
                    )
                }

                {/* CONTACTS */}
                {activeProfileTab === ProfileTab.Contacts
                    && this.props.model.get("PatientProfileEditMode") === EditMode.View
                    && patientStore.CanEditPatient
                    && (
                        <ButtonControl
                            className="btn btn-primary pull-right"
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_EditClick)}
                            label={cacheStore.GetString("EDITLabel")}
                        />
                    )
                }
                {activeProfileTab === ProfileTab.Contacts
                    && this.props.model.get("PatientProfileEditMode") === EditMode.View
                    && patientStore.CanEditPatientEmail
                    && (
                        <ButtonControl
                            className="btn btn-primary pull-right push-right-5"
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_EditEmailsClick)}
                            label={cacheStore.GetString("ChangeEmailsLabel")}
                        />
                    )
                }
                {activeProfileTab === ProfileTab.Contacts
                    && this.props.model.get("PatientProfileEditMode") === EditMode.Edit
                    && (
                        <div>
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_CancelClick)}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                disabled={!canSaveContacts}
                                className="btn btn-primary"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_SaveClick)}
                                label={cacheStore.GetString("SaveLabel")}
                            />
                        </div>
                    )
                }

                {/* CLINICIANS */}
                {activeProfileTab === ProfileTab.Clinicians
                    && this.props.model.get("PatientProfileEditMode") === EditMode.View
                    && (
                        <ButtonControl
                            className="btn btn-primary pull-right"
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Clinicians_EditClick)}
                            label={cacheStore.GetString("EDITLabel")}
                        />
                    )
                }
                {activeProfileTab === ProfileTab.Clinicians
                    && this.props.model.get("PatientProfileEditMode") === EditMode.Edit
                    && (
                        <div>
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Clinicians_CancelClick)}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                disabled={!canSaveClinicians}
                                className="btn btn-primary"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Clinicians_SaveClick)}
                                label={cacheStore.GetString("SaveLabel")}
                            />
                        </div>
                    )
                }

                {/* OTHER INFO */}
                {activeProfileTab === ProfileTab.Other
                    && this.props.model.get("PatientProfileEditMode") === EditMode.View
                    && (
                        <ButtonControl
                            className="btn btn-primary pull-right"
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_EditClick)}
                            label={cacheStore.GetString("EDITLabel")}
                        />
                    )
                }
                {activeProfileTab === ProfileTab.Other
                    && this.props.model.get("PatientProfileEditMode") === EditMode.Edit
                    && (
                        <div>
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_CancelClick)}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                className="btn btn-primary"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.OtherInfo_SaveClick)}
                                label={cacheStore.GetString("SaveLabel")}
                                disabled={!canSaveOther}
                            />
                        </div>
                    )
                }

                {/* RELATIONSHIPS */}
                {activeProfileTab === ProfileTab.Relationships
                    && patientStore.CanEditPatientRelationship
                    && this.props.model.get("PatientProfileEditMode") === EditMode.View
                    && hasRelationships
                    && (
                        <ButtonControl
                            className="btn btn-primary pull-right"
                            onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Relationships_EditClick)}
                            label={cacheStore.GetString("EDITLabel")}
                        />
                    )
                }
                {activeProfileTab === ProfileTab.Relationships
                    && this.props.model.get("PatientProfileEditMode") === EditMode.Edit
                    && (
                        <div>
                            <ButtonControl
                                className="btn btn-default push-right-5"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Relationships_CancelClick)}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                className="btn btn-primary"
                                onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.Relationships_SaveClick)}
                                label={cacheStore.GetString("SaveLabel")}
                            />
                        </div>
                    )
                }
            </div>
        );
    }
}

export { ProfileViewControls };
